import mixpanel from 'mixpanel-browser'
import { get as lodashGet } from 'lodash'
import moment from 'moment'
import * as Sentry from '@sentry/vue'
import UserApi from '@/api/user'
import store from '@/vuex/store'
import config from '@/config'
import Util from '@/util'
import PresentationMixpanelTracker from '@/utils/mixpanel/presentation.mixpanel-tracker'
import cookie, { NEW_USER_COOKIE, PRICING_VERSION_COOKIE } from '@/services/cookie'
import { localGetters } from '@/utils/abtest.utils'
import { PUBLIC_ROUTES } from '@/constant/routes'
import { pushToQueue } from '@/utils/deferredAction.queue'
import Socket from '@/socket'

// AHA-43087: fires from RegisterV3/LoginV3 right before each post-auth
// navigation so we can attribute where users go after sign-up/login. Lexicon
// entry lives in Mixpanel EU project 2668743.
export const POST_SIGNUP_ROUTE_DECISION = 'post_signup_route_decision'

const mixpanelAvailable = config.mixpanel && config.mixpanel.projectToken
let isInitialized = false
let isUserIdentified = false

/**
 * Queue mechanism for Mixpanel events to ensure Unleash feature flags
 * are initialized before tracking occurs.
 * This prevents loss of cohort data and ensures accurate tracking.
 */

const isAdmin = () => {
  return store.state.isAdmin || window.location.href.includes('admin=true') ||
    sessionStorage.getItem('isAdmin') === 'true'
}

const register = (props) => {
  try {
    if (mixpanelAvailable && !isAdmin() && isInitialized) {
      mixpanel.register(props)
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

const unregister = (prop) => {
  try {
    if (mixpanelAvailable && isInitialized) {
      mixpanel.unregister(prop)
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}
/**
 * thread together the UTM and referrer of landing site and presenter app
 * 1. UTM params on presenter app url takes precedence
 *  . if no UTM params on presenter app url, use UTM params from landing site cookie:
 * 2. Referrer: if referrer == landing site, referrer from landing site
 *    Else do nothing
 */
const setUtmAndReferrerSuperProps = () => {
  const generalCookieName = 'aha_landing_site'
  const keepUtmSessionStorageKey = 'keepUtmPersistence'
  const urlParams = new URLSearchParams(window.location.search)

  // 1. Read incoming UTM params from the URL
  const utm_campaign = urlParams.get('utm_campaign')
  const utm_medium = urlParams.get('utm_medium')
  const utm_source = urlParams.get('utm_source')

  // 2. Read unified cookie
  const raw = cookie.getValue(generalCookieName)
  let generalParams = {}
  try {
    generalParams = raw ? JSON.parse(raw) : {}
  } catch (_) {
    generalParams = {}
  }

  // 3. UTM handling
  if (utm_campaign || utm_medium || utm_source) {
    // New UTM present in the URL → clear the cookie so Mixpanel sees the fresh params
    cookie.deleteValue(generalCookieName)
    sessionStorage.setItem(keepUtmSessionStorageKey, 'true')
  } else if (generalParams.utm_source || generalParams.utm_medium || generalParams.utm_campaign) {
    // No UTM in URL, but we have them in our cookie → register into Mixpanel
    register({
      utm_campaign: generalParams.utm_campaign,
      utm_medium: generalParams.utm_medium,
      utm_source: generalParams.utm_source,
    })
    // then clear the cookie so it doesn't get re‑sent, and remember we did it
    cookie.deleteValue(generalCookieName)
    sessionStorage.setItem(keepUtmSessionStorageKey, 'true')
  } else if (!sessionStorage.getItem(keepUtmSessionStorageKey)) {
    // by default Mixpanel persist utm campaign params for as long as its cookies last (typically 1 year)
    // we want to turn off this behavior
    // stop_utm_persistence: true is an option
    // but we will need to send a event in case utm params are in cookie not in url
    ['utm_campaign', 'utm_medium', 'utm_source']
      .forEach((key) => unregister(key))
  }

  // 4. Referrer handling
  // set referrer to Mixpanel context
  // only if the document.referrer is pointing to landing site
  // since we will be more interested in the referrer of the landing site
  // eslint-disable-next-line max-len
  const isFromLandingSite = generalParams.landingSite && document.referrer?.startsWith(generalParams.landingSite)
  const landingReferrer = generalParams.referrer && isFromLandingSite ? generalParams.referrer : null
  if (landingReferrer) {
    register({
      $referrer: landingReferrer,
      $referring_domain: new URL(landingReferrer).hostname,
    })
  }

  // 5. Session‑level referrer (first ever referrer this session)
  // store the first referrer in sessionStorage
  // to ignore navigation within the app workflow like login, checkout ...
  let session_referrer = sessionStorage.getItem('session_referrer')
  if (session_referrer === undefined || session_referrer === null) {
    session_referrer = landingReferrer || document.referrer || ''
    sessionStorage.setItem('session_referrer', session_referrer)
  }

  let session_referring_domain = '$direct'
  try {
    if (session_referrer) session_referring_domain = new URL(session_referrer).hostname
  } catch (e) {
    // Invalid URL, fallback to $direct
  }

  register({
    session_referrer: session_referrer || '$direct',
    session_referring_domain,
  })
}

const setPricingVersionSuperProps = () => {
  // Get pricing_version from cookie and register it as super property
  const pricingVersion = cookie.getValue(PRICING_VERSION_COOKIE)

  if (typeof pricingVersion === 'string' && pricingVersion) {
    register({
      pricing_version: pricingVersion,
    })
  }
}

// AHA-46211/AHA-47232: aha_homepage_variant and aha_homepage_phase are set by a CloudFront
// Function on the landing site, .ahaslides.com domain; aha_homepage_phase is an epoch that
// re-randomises everyone into new variants when bumped.
// Combined into one "phase<N>:<variant>" string because people.set_once locks per KEY: two
// separate keys would let a later, differently-randomised load complete a stray key and stamp a
// pair nobody was actually served.
// Two names, two scopes: homepage_ab_exposure is browser-scoped and overwritten on every init()
// (anonymous funnel only, never a user-level metric); homepage_ab_cohort is the frozen
// acquisition cohort every user-level metric reads.
// Super properties persist past the cookie's 30-day life, hence the unregister when the pair is
// gone. cookie.getValue() prefixes keys with `eu-` when VUE_APP_REGION=eu.
const HOMEPAGE_AB_EXPOSURE_PROP = 'homepage_ab_exposure'
const HOMEPAGE_AB_COHORT_PROP = 'homepage_ab_cohort'

// Shared by both functions below: reads the two cookies passed in and returns the single
// "phase<N>:<variant>" string, or null when there is no cohort (either cookie missing, empty, or
// not a string - cookie.getValue() coerces the literal cookie strings 'true'/'false' into the
// booleans true/false, so a truthy non-string value is reachable here and must read as absent).
const buildHomepageAbCohortValue = (variant, phase) => {
  if (typeof variant !== 'string' || !variant || typeof phase !== 'string' || !phase) {
    return null
  }
  return `phase${phase}:${variant}`
}

const setHomepageAbProps = () => {
  let variant
  let phase
  try {
    variant = cookie.getValue('aha_homepage_variant')
    phase = cookie.getValue('aha_homepage_phase')
  } catch (error) {
    // Add-in iframes (PowerPoint, Google Slides) can throw reading cookies; treat as no arm.
    Sentry.captureException(error)
  }

  const value = buildHomepageAbCohortValue(variant, phase)
  if (!value) {
    unregister(HOMEPAGE_AB_EXPOSURE_PROP)
    return
  }

  register({ [HOMEPAGE_AB_EXPOSURE_PROP]: value })
}

// Writes homepage_ab_cohort, the user-profile counterpart of the super property above. Called
// ONLY from the three registration call sites (RegisterV3, Auth, Token), right next to
// cookie.setValue(NEW_USER_COOKIE, true), so it fires exactly once per genuine new account and
// never on a login. people.set_once means a phase bump that re-randomises an already-registered
// user's browser can never rewrite their original cohort.
//
// Does NOTHING when there is no cohort: no partial value, and no unregister call either. Unlike
// the super property, a user acquired without the cookie pair has no cohort forever - there is no
// later state for this property to fall back to, so there is nothing to clear.
//
// SAFE TO CALL BEFORE mixpanel.identify(): mixpanel-browser queues every people.* call until
// identify() has been called (node_modules/mixpanel-browser/src/mixpanel-people.js:335,
// `if (!this._identify_called()) { this._enqueue(data); return }`) and flushes the queue onto the
// real profile at identify time. So the call site does not need to sequence this after identify()
// - do not "fix" this into a race-avoidance ordering, there is no race to avoid.
const setHomepageAcquisitionCohort = () => {
  try {
    if (!mixpanelAvailable || isAdmin() || !isInitialized) return
    const variant = cookie.getValue('aha_homepage_variant')
    const phase = cookie.getValue('aha_homepage_phase')
    const value = buildHomepageAbCohortValue(variant, phase)
    if (!value) return
    mixpanel.people.set_once({ [HOMEPAGE_AB_COHORT_PROP]: value })
  } catch (error) {
    Sentry.captureException(error)
  }
}

// Which PowerPoint host this frame is running in — 'PC', 'Mac' or 'OfficeOnline' (constant/office).
// Named for the store key it mirrors (state.addinPPTPlatform) so the two are greppable together.
const ADDIN_PLATFORM_PROP = 'pptAddinPlatform'

// A super property because the add-in's behaviour genuinely FORKS on the platform — desktop
// resolves the shown slide from slide tags, the web from a localStorage bridge — so any add-in
// metric that cannot be split by host is two different numbers averaged together. Until now the
// only add-in signal in Mixpanel was the `PPT Addin > ` event-name prefix, which cannot be
// segmented.
//
// CALLED FROM TWO PLACES, and neither alone is enough. init() runs from a cookie-consent callback
// and captureOfficePlatform() from the Office load path, and either can win the race: registering
// only at capture time silently no-ops whenever consent has not landed yet (register() requires
// isInitialized), and registering only at init time misses every add-in that loads after consent.
// Both calls are idempotent — mixpanel.register overwrites.
//
// UNREGISTERS when there is no platform, which is the half that is easy to miss. Super properties
// PERSIST (localStorage when canUseMixpanelLocalStorage), and the add-in shares an origin with the
// ordinary presenter app — so without this, one add-in session would keep tagging every later
// non-add-in session in the same browser as if it had come from PowerPoint. Clearing on any load
// that is not an add-in load is what keeps the property meaning what it says.
const setAddinPlatformSuperProps = () => {
  const platform = store.state.addinPPTPlatform

  if (typeof platform === 'string' && platform) {
    register({
      [ADDIN_PLATFORM_PROP]: platform,
    })
    return
  }
  unregister(ADDIN_PLATFORM_PROP)
}

const init = (additionalOptions = {}) => {
  try {
    if (mixpanelAvailable) {
      mixpanel.init(config.mixpanel.projectToken, {
        debug: config.env !== 'production' || localStorage.getItem('mt-enabled') === 'true',
        api_host: config.mixpanel.proxyUrl,
        record_sessions_percent: 1,
        record_max_ms: 10000,
        ...additionalOptions,
      })
      isInitialized = true
      const superPropSetters = [
        setUtmAndReferrerSuperProps,
        setPricingVersionSuperProps,
        // AHA-47232: home_page_variant is dead, the landing site stopped writing this cookie.
        // Actively clear the super property so browsers that already have it sitting in
        // localStorage stop stamping it on every event.
        () => unregister('home_page_variant'),
        setHomepageAbProps,
        // A pre-init registration call can miss isInitialized (init awaits async consent).
        // Retry here, gated on aha-new-user so only genuine signups retry; set_once makes a
        // successful first attempt's retry a harmless no-op.
        () => { if (cookie.getValue(NEW_USER_COOKIE)) setHomepageAcquisitionCohort() },
        setAddinPlatformSuperProps,
      ]
      // Each one isolated: these are independent tags, and one bad cookie should not cost
      // the others. A shared catch used to drop every setter after the thrower.
      superPropSetters.forEach((setSuperProps) => {
        try {
          setSuperProps()
        } catch (error) {
          Sentry.captureException(error)
        }
      })
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}
const start_session_recording = () => {
  try {
    if (mixpanelAvailable) {
      mixpanel.start_session_recording()
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}
const stop_session_recording = () => {
  try {
    if (mixpanelAvailable) {
      mixpanel.stop_session_recording()
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

// Attach the current socket id to every tracked event so a session can be
// correlated with the socket that emitted it. Read live at call time so it
// always reflects the active connection (socket.id changes across reconnects).
const withSocketId = (eventProps) => {
  // getSocketId() is null-safe (returns undefined before the socket connects), so
  // this never throws even when an event fires early in the app lifecycle. socketId
  // is spread first so a caller that sets its own socketId still takes precedence.
  const socketId = Socket.getSocketId()
  return { ...(socketId ? { socketId } : {}), ...eventProps }
}

const track = (eventName, eventProps) => {
  if (!mixpanelAvailable || isAdmin() || !eventName) return

  // Snapshot props at call time. When the relevant feature flag isn't loaded
  // yet, trackAction is queued and flushed later (AHA-40717). Keeping the
  // original reference meant any later mutation/reassignment of the caller's
  // object dropped feature_name/event_label from deferred events.
  const propsSnapshot = withSocketId(eventProps)

  const trackAction = () => {
    try {
      mixpanel.track(eventName, propsSnapshot)
    } catch {
      // do nothing
    }
  }

  const currentRouteName = store.state.route?.name
  const isPublicRoute = PUBLIC_ROUTES.includes(currentRouteName)

  if (isPublicRoute) {
    if (store.state.featureToggles?.hasLoadedAnonymousFlag) {
      trackAction()
    } else {
      pushToQueue('mixpanel_anonymous', trackAction)
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (store.state.featureToggles?.hasLoadedAuthFlag) {
      trackAction()
    } else {
      pushToQueue('mixpanel_auth', trackAction)
    }
  }
}

const trackForLoginUser = (eventName, eventProps) => {
  if (!mixpanelAvailable || !isUserIdentified || !eventName) return

  // Snapshot props at call time — see note in track() above (AHA-40717).
  const propsSnapshot = withSocketId(eventProps)

  const trackAction = () => {
    try {
      mixpanel.track(eventName, propsSnapshot)
    } catch {
      // do nothing
    }
  }

  // trackForLoginUser is always for auth phase
  if (store.state.featureToggles?.hasLoadedAuthFlag) {
    trackAction()
  } else {
    pushToQueue('mixpanel_auth', trackAction)
  }
}

const timeEvent = (eventName) => {
  if (mixpanelAvailable && eventName) {
    try {
      mixpanel.time_event(eventName)
    } catch {
      // do nothing
    }
  }
}

const immutableUserProperties = (userData) => {
  const props = {
    abTestGroup: userData.id % 4,
    $created: userData.createdAt,
    signUpSource: userData.source,
    referralUserId: userData.referralUserId,
  }

  if (userData.country) {
    props.$country_code = userData.country
  }
  if (userData.city) {
    props.$city = userData.city
  }
  return props
}

const mutableUserProperties = (userData) => {
  const props = {
    $email: userData.email,
    $first_name: userData.firstName,
    $last_name: userData.lastName,
    useAhaSlidesFor: userData.useAhaSlidesFor,
    userProfession: userData.userProfession,
    plan: userData.plan,
    firstEventStartedAt: userData.firstEventStartedAt,
    lastEventStartedAt: userData.lastEventStartedAt,
    teamRole: userData.accountRole,
    isTeamUser: !!userData.accountId,
    stripeId: userData.stripeCustomerId,
  }
  if (userData.abTest) {
    props.abTest = userData.abTest
  }
  if (userData.firstPage) {
    props.firstPage = userData.firstPage
  }
  if (userData.personalInformation && userData.personalInformation.presenterLanguage) {
    props.presenterLanguage = userData.personalInformation.presenterLanguage
  }
  return props
}

// Identity/PII must live only on the Mixpanel People profile (people.set/set_once),
// never replicated as a super property onto every subsequent event.
const SUPER_PROPERTY_PII_KEYS = ['$email', '$first_name', '$last_name']

const withoutPiiSuperProperties = (props) => {
  const superProps = { ...props }
  SUPER_PROPERTY_PII_KEYS.forEach((key) => delete superProps[key])
  return superProps
}

// AHA-41872: tag each user with the onboarding variant they were exposed to.
// Uses people.set_once so the first observed value sticks — protects experiment
// integrity if the user re-enters the flow or rollout dials change.
//
// Valid values:
//   'no-question' | 'use-case' | 'profession'  — Unleash variant resolved
//   'fallback'                — Unleash flag returned an invalid value and
//                               child app fell back to registryKeys[0]
const setOnboardingVariantOnce = (variant) => {
  if (!variant) return
  if (!mixpanelAvailable || isAdmin() || !isInitialized) return
  try {
    mixpanel.people.set_once({ onboardingVariant: variant })
  } catch (e) {
    // do nothing if mixpanel set_once fails
  }
}

const identify = (user) => {
  if (mixpanelAvailable && user && !isAdmin()) {
    try {
      if (!localGetters.canUseMixpanelLocalStorage()) {
        // alias is a legacy method. Identify is supposed to do the joining of distinct_id and user_id
        // into a single cluster.
        // take this opportunity to remove alias
        if (user && user.id && String(mixpanel.get_distinct_id()) !== String(user.id)) {
          mixpanel.alias(user.id)
        }
      }
      mixpanel.identify(user.id)
      const isTeamUser = !!user.accountId

      user.isTeamUser = isTeamUser
      user.useAhaSlidesFor = lodashGet(user, 'personalInformation.useAhaSlidesFor', null)
      user.userProfession = lodashGet(user, 'personalInformation.userProfession', null)

      // Set user profile properties
      mixpanel.people.set_once({
        $distinct_id: user.id,
        ...immutableUserProperties(user),
      })
      mixpanel.people.set({
        ...mutableUserProperties(user),
      })

      // Clear PII a pre-fix build may have persisted as super properties, so
      // returning users are cleaned up too — not just newly-identified ones.
      SUPER_PROPERTY_PII_KEYS.forEach((key) => unregister(key))
      mixpanel.register_once(immutableUserProperties(user))
      mixpanel.register(withoutPiiSuperProperties(mutableUserProperties(user)))
      isUserIdentified = true
    } catch (e) {
      // do nothing if mixpanel can not identify user
    }
  }
}

const setTeamProps = (team) => {
  if (mixpanelAvailable && team && !isAdmin()) {
    try {
      mixpanel.register({
        teamPlan: team.plan || 'free',
        isSelfCreatedTeam: team.createdFrom === 'presenter',
      })
    } catch (e) {
      // do nothing if mixpanel can not identify user
    }
  }
}

const getPresentationContext = (presentation) => {
  if (!presentation) return {}
  // We only need the following fields for presentation context, dont need all fields
  const lastSlide = presentation.slides && presentation.slides.length ?
    presentation.slides[presentation.slides.length - 1] :
    null
  return {
    presentationId: presentation.id,
    accessCode: presentation.accessCode,
    uniqueAccessCode: presentation.uniqueAccessCode,
    areSlideOptionsShuffling: presentation.areSlideOptionsShuffling,
    isUsingAudienceAdmission: !!presentation.audienceAdmission,
    audiencePacing: presentation.audiencePacing,
    createdAt: presentation.createdAt,
    modifiedAt: presentation.modifiedAt,
    updatedAt: presentation.updatedAt,
    enableCopySlideNote: presentation.enableCopySlideNote,
    enableQuizCountdown: presentation.enableQuizCountdown,
    presentationLanguage: presentation.language,
    filteringProfanity: presentation.filteringProfanity,
    isEnableChat: presentation.isEnableChat,
    isEnableQuizMusic: presentation.isEnableQuizMusic,
    isModerationMode: presentation.isModerationMode,
    isReactionEnabled: presentation.isReactionEnabled,
    presenting: presentation.presenting,
    privateMode: presentation.privateMode,
    qnaAllSlide: presentation.qnaAllSlide,
    qnaAnonymous: presentation.qnaAnonymous,
    qnaAudienceShowAll: presentation.qnaAudienceShowAll,
    showAhaSlidesCTA: presentation.showAhaSlidesCTA,
    showHyperLink: presentation.showHyperLink,
    slideCount: presentation.slideCount,
    sourcePresentation: presentation.sourcePresentation,
    manualRevealCorrectAnswers: presentation.manualRevealCorrectAnswers,
    tags: presentation.tags,
    teamPlay: presentation.teamPlay,
    teamCount: presentation.teamCount,
    teamSize: presentation.teamSize,
    teamScoringRule: presentation.teamScoringRule,
    whiteLabelPresenterDomain: presentation.whiteLabelPresenterDomain,
    whiteLabelLink: presentation.whiteLabelLink,
    isPublicSearch: presentation.isPublicSearch,
    lastSlideType: lastSlide ? lastSlide.type : undefined,
  }
}

const getPlanContext = (plan) => {
  if (!plan) return {}
  return {
    planType: plan.type,
    planGroup: plan.group,
    planBillingCycle: plan.billingCycle,
  }
}

const unregisterPresentationContext = () => {
  const presentationContextProps = [
    'presentationId',
    'accessCode',
    'uniqueAccessCode',
    'areSlideOptionsShuffling',
    'isUsingAudienceAdmission',
    'audiencePacing',
    'createdAt',
    'modifiedAt',
    'updatedAt',
    'enableCopySlideNote',
    'enableQuizCountdown',
    'presentationLanguage',
    'filteringProfanity',
    'isEnableChat',
    'isEnableQuizMusic',
    'isModerationMode',
    'isReactionEnabled',
    'presenting',
    'privateMode',
    'qnaAllSlide',
    'qnaAnonymous',
    'qnaAudienceShowAll',
    'showAhaSlidesCTA',
    'showHyperLink',
    'slideCount',
    'sourcePresentation',
    'tags',
    'teamPlay',
    'teamCount',
    'teamSize',
    'teamScoringRule',
    'whiteLabelPresenterDomain',
    'whiteLabelLink',
  ]

  for (let i = 0; i < presentationContextProps.length; i++) {
    unregister(presentationContextProps[i])
  }
}

const getHostingEventInsertId = (userId, presentationId, eventTime, isUserFirstEvent) => {
  const hour = eventTime.getHours()
  if (isUserFirstEvent) {
    return `aha-${userId}-first`
  }
  // $insert_id max len = 36 chars, so keep it short
  return `aha-${presentationId}-${hour}`
}

const setHostingEventTime = (presentationId, eventTime) => {
  if (!presentationId || !eventTime) return
  localStorage.setItem(`host-event-time-${presentationId}`, eventTime)
}
const getHostingEventTime = (presentationId) => {
  if (!presentationId) return null
  return +localStorage.getItem(`host-event-time-${presentationId}`) || null
}

const trackHostingEvent = async ({
  presentation, onlineCount, maxOnlineCount, routeName,
}) => {
  // Record mixpanel event for Hosting event
  // Cần phân biệt việc click nút Present với actual Hosting event
  // Để được coi là hosting event, thì event đó phải có số online count > 3
  // và tạm coi một event diễn ra trong 1h, trong timebox 1h này chỉ tính duy nhất 1 lần
  // việc start hosting event
  // Chỉ track khi onlineCount có xu hướng đi lên
  const START_HOSTING_EVENT = 'Start hosting event'
  const user = presentation.TakenOverByUser || presentation.User
  if (user && onlineCount > 3 &&
    (!maxOnlineCount || onlineCount > maxOnlineCount)
  ) {
    const eventTime = new Date()
    // This issue happened long ago due to we store the moment object inside vuex state.
    // then the mixpanel get the data from it. (inside getCurrentUserAction)
    // Solution: turn it into isostring (still need to put a flag because I’ve changed the data type)
    const canUseIsoString = store.getters['featureToggles/canUseISOStringForEventStartedAt']
    const eventStartedAt = canUseIsoString
      ? moment(eventTime).utc().toISOString()
      : moment(eventTime).utc()
    // For old users that don't have the 'firstEventStartedAt' field,
    // we should count the hosted presentations that they already have
    // count it on App.vue
    const isUserFirstEvent = !user.firstEventStartedAt && !store.state.userHasHostedEvents
    const presentationTracker = new PresentationMixpanelTracker(presentation, routeName, store.state.currentUser)
    if (isUserFirstEvent) {
      await UserApi.patchUserById(user.id, {
        firstEventStartedAt: eventStartedAt,
        lastEventStartedAt: eventStartedAt,
      })
      store.state.currentUser.firstEventStartedAt = eventStartedAt
      store.state.currentUser.lastEventStartedAt = eventStartedAt
      const presentationOwnerId = presentation.userId
      let isPresentationOwner = false
      if (presentation.userId === store.state.currentUser.id) {
        isPresentationOwner = true
      }
      let presentationOwnerCurrentPlan = null
      if (presentation.User) {
        presentationOwnerCurrentPlan = presentation.User.plan
      }
      track(START_HOSTING_EVENT, {
        $insert_id: getHostingEventInsertId(
          user.id,
          presentation.id,
          eventTime,
          true,
        ),
        isUserFirstEvent,
        presentationOwnerId,
        isPresentationOwner,
        presentationOwnerCurrentPlan,
        ...presentationTracker.getCustomPropsTrackStartPresenting(),
      })

      // AHA-21083 Add trigger for GTM
      window.dataLayer.push({
        event: START_HOSTING_EVENT,
        isUserFirstEvent,
      })
    } else {
      const lastHostEventTime = getHostingEventTime(presentation.id)
      const ONE_HOUR = 3600e3
      const now = new Date().getTime()
      if (!lastHostEventTime || (now > lastHostEventTime + ONE_HOUR)) {
        await UserApi.patchUserById(user.id, {
          lastEventStartedAt: eventStartedAt,
        })

        const presentationOwnerId = presentation.userId
        let isPresentationOwner = false
        if (presentation.userId === store.state.currentUser.id) {
          isPresentationOwner = true
        }
        let presentationOwnerCurrentPlan = null
        if (presentation.User) {
          presentationOwnerCurrentPlan = presentation.User.plan
        }
        track(START_HOSTING_EVENT, {
          $insert_id: getHostingEventInsertId(
            user.id,
            presentation.id,
            eventTime,
            false,
          ),
          presentationOwnerId,
          isPresentationOwner,
          presentationOwnerCurrentPlan,
          ...presentationTracker.getCustomPropsTrackStartPresenting(),
        })

        // AHA-21083 Add trigger for GTM
        window.dataLayer.push({
          event: START_HOSTING_EVENT,
        })

        setHostingEventTime(presentation.id, now)
      }
    }
    if (store.state.isReferPublicTemplate) {
      track(START_HOSTING_EVENT, { eventCategory: 'Public template > Demo mode' })
    }
  }
}

const trackCharge = (amount, properties = {}) => {
  if (mixpanelAvailable) {
    const { discountWithCredit, discountWithCoupon, totalAmount } = properties
    const convertProperties = {
      discountWithCredit: discountWithCredit || 0,
      discountWithCoupon: discountWithCoupon || 0,
      totalAmount: totalAmount || 0,
      customerLifetimeValue: amount,
    }
    mixpanel.people.track_charge(amount, convertProperties)
    mixpanel.people.increment(convertProperties)
  }
}

const getChargeContext = (data) => {
  let {
    paidAmount, credit, coupon,
  } = data

  paidAmount = parseFloat(paidAmount)
  credit = Math.abs(credit) || 0
  coupon = Math.abs(coupon) || 0
  const totalAmount = parseFloat(Util.roundNumber(paidAmount + credit + coupon))

  return {
    discountWithCredit: credit,
    discountWithCoupon: coupon,
    paidAmount,
    totalAmount,
  }
}

const trackTimeEvent = (eventName) => {
  try {
    mixpanel.time_event(eventName)
  } catch (error) {
    Sentry.captureException(error)
  }
}

const reset = () => {
  try {
    if (mixpanelAvailable && isInitialized) {
      mixpanel.reset()
      isUserIdentified = false
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

const getProperties = () => {
  try {
    if (mixpanelAvailable && isInitialized) {
      const mixpanelProps = { distinct_id: mixpanel.get_distinct_id() }
      const keys = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        '$device_id',
        '$referrer',
        '$referring_domain',
        'session_referrer',
        'pricing_version',
      ]
      keys.forEach((key) => {
        if (mixpanel.get_property(key)) {
          mixpanelProps[key] = mixpanel.get_property(key)
        }
      })
      return mixpanelProps
    }
  } catch (error) {
    Sentry.captureException(error)
    return {}
  }
}

const isReady = () => {
  return isInitialized
}

const isLoggedInUserIdentified = () => {
  return isUserIdentified
}

// AHA-43087: thin wrapper called from RegisterV3 / LoginV3 immediately before
// every post-auth navigation. Wrapped in try/catch so a transient failure inside
// Mixpanel / store cannot block the navigation that is about to happen on the
// next line at the call site.
const trackPostSignupRoute = (route) => {
  try {
    track(POST_SIGNUP_ROUTE_DECISION, {
      route,
    })
  } catch (e) {
    try { Sentry.captureException(e) } catch { /* ignore */ }
  }
}

export default {
  init,
  track,
  register,
  setAddinPlatformSuperProps,
  setHomepageAbProps,
  setHomepageAcquisitionCohort,
  identify,
  getPresentationContext,
  getPlanContext,
  unregisterPresentationContext,
  mixpanelAvailable,
  unregister,
  trackHostingEvent,
  trackCharge,
  getChargeContext,
  trackTimeEvent,
  reset,
  setTeamProps,
  setOnboardingVariantOnce,
  start_session_recording,
  stop_session_recording,
  getProperties,
  isReady,
  timeEvent,
  isLoggedInUserIdentified,
  trackForLoginUser,
  trackPostSignupRoute,
}
